/* eslint-disable max-len */
import React, { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { createUseStyles } from 'react-jss'
import { useSearchParams } from 'react-router-dom'
import { Member } from '../../../models/Member'
import { IFilter, initialSelectedFilters } from '../../../models/Filter'
import { membersAtom } from '../../../api/members/members'
import {
    Card,
    CardCell,
    CardList,
    CardMobile,
    CardHeaderCell,
    CardHeader,
    CardContentMobile,
    CircleText,
    Circle,
} from '../../ui/Card'
import { Typography } from '../../ui/Typography/Typography'
import { getInitials } from '../../../utils/initials'
import { Badge } from '../../ui/Badge/Badge'
import { Favorite } from '../../ui/Favorite'
import { removeFavorite, setFavorite } from '../../../api/favorite'
import { NoteButton } from '../../ui/NoteButton'
import { Filter } from './Filter'
import {
    filterAtom,
    filteredAtom,
    searchAtom,
    showOnlyFavorites,
    filterSelected,
} from '../../../store/filter/filterAtom'
import { FilterSummary } from '../../modules/Filter/FiltersSummary'
import { Button } from '../../ui/Button/Button'
import { colors } from '../../../theme/palette'
import { SendFill, SendToMany, Star, StarFill } from '../../../assets/icons'
import breakpoints from '../../../theme/breakpoints'
import { SortButton } from '../../ui/SortButton'
import useSortMembersList from '../../../hooks/useSortMembersList'
import SearchField from '../../ui/SearchField/SearchField'
import { formatDate } from '../../../utils/format'
import Checkbox from '../../ui/FormElememts/Checkbox'
import { Dialog } from '../../ui/Dialog/Dialog'
import { L, L_HTML } from '../../providers/Lokalise'
import { baseButton } from '../../../theme/reuseables'
import { HeaderLinks } from '../../modules/Headerlinks'
import { profileAtom } from '../../../api/profile/profile'
import { ImitatorNoAccessWarning } from '../../modules/Imitator/ImitatorNoAccessWarning'
import { ContactFlow } from './ContactFlow'

const getUpdateStatusLabel = (status: string): string => {
    let label = status

    switch (status) {
        case 'AfsluttetBehandlingSidste30Dage':
            label = L('MINEOPDATERINGER_STATUS_FaerdigBehandlet') // 'Færdig behandlet / opdateret'
            break
        case 'AfventerGodkendelse':
            label = L('MINEOPDATERINGER_STATUS_UnderOpdatering') // Afventer opdatering'
            break
        default:
            break
    }

    return label
}

type IData = {
    filter: IFilter
    members: Member[]
}

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    container: {
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '0',
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
        flexDirection: 'column',
        gap: '10px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                flexDirection: 'row',
                gap: '0',
            },
        },
    },
    sendingEmailSettings: {
        display: 'flex',
        padding: '.5rem 1rem',
        minHeight: '5rem',
        width: '100%',
        backgroundColor: colors.green200,
        color: colors.black,
        justifyContent: 'space-between',
        alignItems: 'center',
        '& span': {
            fontWeight: 700,
        },
        animation: '.5s $fadeIn',
    },
    members: {
        paddingTop: '20px',
    },
    sortPlaceholder: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.75em 1.75em',
        margin: '0 0.3em 0.3em 0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        boxShadow: 0,
        '&:hover': {
            cursor: 'pointer',
            color: colors.black,
            fontWeight: '700',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        fontStyle: 'normal',
        fontSize: '16px',
        lineHeight: '120%',
        color: '#000000',
        border: 0,
        background: 'none',
    },
    dialogButtonsContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-around',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    modalButtons: {
        display: 'flex',
    },
    modalCheckbox: {
        display: 'flex',
        position: 'absolute',
        right: '20px',
        top: '20px',
    },
    numberOfMembers: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconWrapper: {
        position: 'relative',
    },
    searchField: {
        width: '100%',
        marginRight: '20px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                width: 'auto',
                minWidth: '425px',
                marginRight: '20px',
            },
        },
    },
})

const gridTemplateColumns = '8% 24% 24% 12% 8% 16% 8%'

const Members: React.FC = (): JSX.Element => {
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [data, update] = useAtom(membersAtom)
    const classes = useStyles()
    const [members] = useAtom(filteredAtom)
    const [, setFilter] = useAtom(filterAtom)
    const [, setSearchAtom] = useAtom(searchAtom)
    const [isSendingView, setIsSendingView] = useState<boolean>(false)
    const [AllChosen, setAllChosen] = useState<boolean>(false)
    const [chosenMembers, setChosenMembers] = useState<Member[]>([])
    const [favoritesOnly, setShowOnlyFavorites] = useAtom(showOnlyFavorites)
    const [, setFilterSelected] = useAtom(filterSelected)
    const [searchParams] = useSearchParams()
    const [initialized, setInitialized] = useState(false)
    const [authProfile] = useAtom(profileAtom)
    const [firstTimeSendingEmail, setfirstTimeSendingEmail] = useState(true)
    const [showFirstTimeModal, setHowFirstTimeShowModal] = useState(false)
    const FIRST_TIME_EMAIL_KEY = 'first_time_email'
    const [isFlowOpen, setIsFlowOpen] = useState(false)

    const { setSort, sortState } = useSortMembersList()

    useEffect(() => {
        const localStorageData = localStorage.getItem(FIRST_TIME_EMAIL_KEY)

        if (localStorageData) {
            setfirstTimeSendingEmail(false)
        }
    }, [])

    useEffect(() => {
        const d = data as IData
        if (d && d.filter && !initialized) {
            const wp = searchParams.get('wp')
            setInitialized(true)
            if (wp) {
                const found = d.filter.workplaces.find((w) => w.id === +wp)

                if (found) {
                    const copy = { ...initialSelectedFilters }
                    copy.workplaces.push(found)
                    setFilterSelected(copy)
                }
            }

            d.filter.updateStatus = [
                {
                    id: 'AfsluttetBehandlingSidste30Dage',
                    name: 'Opdateret',
                },
                { id: 'AfventerGodkendelse', name: 'Afventer opdatering' },
            ]

            setFilter(d.filter)

            if (!sortState) {
                setSort('name')
            }
        }
    }, [data, setFilter])

    const onClickFavorite = async (member: Member): Promise<void> => {
        const callback = member.isFavorite ? removeFavorite : setFavorite

        const response = await callback(`${member.memberId}`, 'MedlemIdent')

        if (response) {
            update({ type: 'refetch' })
        }
    }

    const isChecked = (member: Member): boolean =>
        chosenMembers.some((element) => element.memberId === member.memberId)

    const handleSendingEmailView = (): void => setIsSendingView((v) => !v)

    const handleChooseAll = (): void => {
        if (chosenMembers.length === members.length) {
            setChosenMembers([])
            setAllChosen(false)
        } else {
            setChosenMembers([...members])
            setAllChosen(true)
        }
    }

    const onSearch = (search: string): void => {
        setSearchAtom(search)
    }

    const useSearch = (search: string): void => {
        setSearchAtom(search)
    }

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks />
                </div>
            </div>
            <div className={classes.headerArea}>
                <div className="desktop-only">
                    <Typography tag="h1" size="XXXXL">
                        {L_HTML('MINEMEDLEMMER_TITEL')}
                    </Typography>
                </div>

                <div className={classes.headerActions}>
                    <div className={classes.searchField}>
                        <SearchField
                            autoFocus
                            label={L('MINEMEDLEMMER_SØGEFELT_LABEL')}
                            required={false}
                            typeSearch="primary"
                            searchButtonLabel={L(
                                'MINEMEDLEMMER_SØGEFELT_LABEL'
                            )}
                            onSearch={onSearch}
                            onChange={useSearch}
                            // style={{ minWidth: '425px', marginRight: '20px' }}
                        />
                    </div>

                    <Filter />
                </div>
            </div>
            <div className={`${classes.headerArea} desktop-only`}>
                <FilterSummary />
                <div className={classes.headerActions}>
                    {!isSendingView && (
                        <Button
                            type="secondary"
                            size="large"
                            icon={<SendToMany fill={colors.green500} />}
                            onClick={() => {
                                if (firstTimeSendingEmail) {
                                    setHowFirstTimeShowModal(true)
                                } else {
                                    handleSendingEmailView()
                                }
                            }}
                            style={{ paddingLeft: '10px' }}
                        >
                            {L('MINEMEDLEMMER_KONTAKTFLERE_KNAP')}
                        </Button>
                    )}
                    {!isSendingView && (
                        <Button
                            type={!favoritesOnly ? 'secondary' : 'primary'}
                            size="large"
                            style={{
                                minWidth: '210px',
                                paddingLeft: '10px',
                                paddingRight: '28px',
                            }}
                            icon={
                                !favoritesOnly ? (
                                    <StarFill fill="rgb(241,196,0)" />
                                ) : (
                                    <Star fill={colors.white} />
                                )
                            }
                            onClick={() => setShowOnlyFavorites(!favoritesOnly)}
                        >
                            {!favoritesOnly
                                ? `${L(
                                      'MINEMEDLEMMER_UDVALGTEMEDLEMMER_KNAP_OFF'
                                  )}`
                                : `${L(
                                      'MINEMEDLEMMER_UDVALGTEMEDLEMMER_KNAP_ON'
                                  )}`}
                        </Button>
                    )}
                </div>
            </div>

            {isSendingView && (
                <div className={classes.sendingEmailSettings}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Checkbox
                            label={L(
                                'MINEMEDLEMMER_KONTAKTFLERE_VÆLG-ALLE_KNAP'
                            )}
                            id="5678"
                            isChecked={AllChosen}
                            onChange={handleChooseAll}
                        />
                        <div
                            style={{
                                marginLeft: '1rem',
                                paddingTop: '0.2rem',
                            }}
                        >
                            <span>{chosenMembers.length}</span>{' '}
                            {L_HTML(
                                'MINEMEDLEMMER_KONTAKTFLERE_MEDLEMMER-VALGT_LABEL'
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendFill fill={colors.white} />}
                            // onClick={handleSendingEmailDialog}
                            onClick={() => setIsFlowOpen(true)}
                            disabled={chosenMembers.length === 0}
                        >
                            {L('MINEMEDLEMMER_KONTAKTFLERE_SEND_KNAP')}
                        </Button>
                        <Button
                            type="secondary"
                            size="large"
                            onClick={() => {
                                handleSendingEmailView()
                                setChosenMembers([])
                                setAllChosen(false)
                            }}
                        >
                            {L('MINEMEDLEMMER_KONTAKTFLERE_ANNULLER_KNAP')}
                        </Button>
                    </div>
                </div>
            )}

            <div className={`${classes.members}`}>
                {members && members.length ? (
                    <CardList>
                        <CardHeader gridTemplateColumns={gridTemplateColumns}>
                            <CardHeaderCell />
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('name')}
                                    sort={sortState}
                                    name="name"
                                >
                                    {L('MINEMEDLEMMER_TABEL_KOLONNETITEL_NAVN')}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('arbejdssted')}
                                    sort={sortState}
                                    name="arbejdssted"
                                >
                                    {L(
                                        'MINEMEDLEMMER_TABEL_KOLONNETITEL_ARBEJDSSTED'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <Typography size="XS" weight="bold">
                                    <SortButton
                                        onClick={() => setSort('birthday')}
                                        sort={sortState}
                                        name="birthday"
                                    >
                                        {L(
                                            'MINEMEDLEMMER_TABEL_KOLONNETITEL_FØDSELSDAG'
                                        )}
                                    </SortButton>
                                </Typography>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <Typography size="XS" weight="bold">
                                    <SortButton
                                        onClick={() => setSort('age')}
                                        sort={sortState}
                                        name="age"
                                    >
                                        {L(
                                            'MINEMEDLEMMER_TABEL_KOLONNETITEL_ALDER'
                                        )}
                                    </SortButton>
                                </Typography>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('status')}
                                    sort={sortState}
                                    name="status"
                                >
                                    {L(
                                        'MINEMEDLEMMER_TABEL_KOLONNETITEL_STATUS'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('notes')}
                                    sort={sortState}
                                    name="notes"
                                >
                                    {L(
                                        'MINEMEDLEMMER_TABEL_KOLONNETITEL_NOTER'
                                    )}
                                </SortButton>
                            </CardHeaderCell>
                        </CardHeader>
                        {members.map((member) => (
                            <div key={member.memberId}>
                                <CardMobile
                                    url={
                                        isSendingView
                                            ? undefined
                                            : `/mine-medlemmer/${member.memberId}`
                                    }
                                >
                                    <CardCell>
                                        {isSendingView ? (
                                            <div>
                                                <Checkbox
                                                    id={String(member.memberId)}
                                                    label=""
                                                    isChecked={isChecked(
                                                        member
                                                    )}
                                                    onChange={() => {
                                                        const ml = [
                                                            ...chosenMembers,
                                                        ]
                                                        if (isChecked(member)) {
                                                            const idx =
                                                                ml.findIndex(
                                                                    (m) =>
                                                                        m.memberId ===
                                                                        member.memberId
                                                                )
                                                            if (idx !== -1) {
                                                                ml.splice(
                                                                    idx,
                                                                    1
                                                                )
                                                            }
                                                            setChosenMembers(ml)
                                                        } else {
                                                            ml.push(member)
                                                            setChosenMembers(ml)
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <Circle>
                                                    <CircleText>
                                                        {getInitials(
                                                            member.fullName
                                                        )}
                                                    </CircleText>
                                                    <Favorite
                                                        active={
                                                            member.isFavorite
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            e.preventDefault()
                                                            if (
                                                                authProfile.isImitator
                                                            ) {
                                                                setShowImitatorModal(
                                                                    true
                                                                )
                                                            } else {
                                                                onClickFavorite(
                                                                    member
                                                                )
                                                            }
                                                        }}
                                                    />
                                                </Circle>
                                            </>
                                        )}
                                        {/* <Circle>
                                            <CircleText>
                                                {getInitials(member.fullName)}
                                            </CircleText>
                                            <Favorite
                                                active={member.isFavorite}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    onClickFavorite(member)
                                                }}
                                            />
                                        </Circle> */}
                                    </CardCell>
                                    <CardContentMobile>
                                        <CardCell>
                                            <Typography
                                                tag="h3"
                                                size="M"
                                                spacing=".2rem"
                                                color="rgba(0, 0, 0, 0.9)"
                                            >
                                                {member.fullName}
                                            </Typography>
                                            <Typography
                                                size="XS"
                                                spacing=".2rem"
                                                color="rgba(0, 0, 0, 0.6)"
                                            >
                                                {member?.workPlaces[0]?.name}
                                            </Typography>
                                            <Typography
                                                size="XXS"
                                                spacing=".2rem"
                                                color="rgba(0, 0, 0, 0.4)"
                                            >
                                                {member?.workPlaces[0]?.name}
                                            </Typography>
                                        </CardCell>
                                    </CardContentMobile>
                                </CardMobile>
                                <Card
                                    key={member.memberId}
                                    gridTemplateColumns={gridTemplateColumns}
                                    url={
                                        isSendingView
                                            ? undefined
                                            : `/mine-medlemmer/${member.memberId}`
                                    }
                                >
                                    <CardCell>
                                        {isSendingView ? (
                                            <div>
                                                <Checkbox
                                                    id={String(member.memberId)}
                                                    label=""
                                                    isChecked={isChecked(
                                                        member
                                                    )}
                                                    onChange={() => {
                                                        const ml = [
                                                            ...chosenMembers,
                                                        ]
                                                        if (isChecked(member)) {
                                                            const idx =
                                                                ml.findIndex(
                                                                    (m) =>
                                                                        m.memberId ===
                                                                        member.memberId
                                                                )
                                                            if (idx !== -1) {
                                                                ml.splice(
                                                                    idx,
                                                                    1
                                                                )
                                                            }
                                                            setChosenMembers(ml)
                                                        } else {
                                                            ml.push(member)
                                                            setChosenMembers(ml)
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={classes.iconWrapper}
                                            >
                                                <Circle>
                                                    <CircleText>
                                                        {getInitials(
                                                            member.fullName
                                                        )}
                                                    </CircleText>
                                                </Circle>
                                                <Favorite
                                                    active={member.isFavorite}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                        if (
                                                            authProfile.isImitator
                                                        ) {
                                                            setShowImitatorModal(
                                                                true
                                                            )
                                                        } else {
                                                            onClickFavorite(
                                                                member
                                                            )
                                                        }
                                                    }}
                                                    right="-10px"
                                                />
                                            </div>
                                        )}
                                    </CardCell>
                                    <CardCell>
                                        <Typography size="XS">
                                            {member.fullName}
                                        </Typography>
                                    </CardCell>
                                    <CardCell>
                                        <Typography size="XS">
                                            {member?.primaryWorkPlace?.name}
                                        </Typography>
                                    </CardCell>
                                    <CardCell>
                                        <Typography size="XS">
                                            {formatDate(member.birthday)}
                                        </Typography>
                                    </CardCell>
                                    <CardCell>
                                        <Typography size="XS">
                                            {member.age}
                                        </Typography>
                                    </CardCell>
                                    <CardCell>
                                        {member.status.toLocaleLowerCase() !==
                                        'ikkeopdateret' ? (
                                            <Badge>
                                                {getUpdateStatusLabel(
                                                    member.status
                                                )}
                                            </Badge>
                                        ) : null}
                                    </CardCell>
                                    <CardCell>
                                        <NoteButton
                                            onClick={() => {
                                                return true
                                            }}
                                        >
                                            {member.numberOfNotes}
                                        </NoteButton>
                                    </CardCell>
                                </Card>
                            </div>
                        ))}
                    </CardList>
                ) : null}
            </div>
            {members.length ? (
                <div className={classes.numberOfMembers}>
                    <Typography size="XS">
                        Viser {members.length} medlemmer
                    </Typography>
                </div>
            ) : null}
            {showFirstTimeModal ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<SendToMany width="46px" fill={colors.green500} />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L('MINEMEDLEMMER_KONTAKTFLERE_FØRSTE_GANG_TITEL')}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L('MINEMEDLEMMER_KONTAKTFLERE_FØRSTE_GANG_TEXT')}
                    </Typography>
                    <div className={classes.dialogButtonsContainer}>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                localStorage.setItem(
                                    FIRST_TIME_EMAIL_KEY,
                                    'true'
                                )
                                setfirstTimeSendingEmail(false)
                                setHowFirstTimeShowModal(false)
                                handleSendingEmailView()
                            }}
                        >
                            OK
                        </Button>
                    </div>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
            <ContactFlow
                isOpen={isFlowOpen}
                onSendDone={() => {
                    setIsSendingView(false)
                    setAllChosen(false)
                    setChosenMembers([])
                    setIsFlowOpen(false)
                }}
                onClose={() => {
                    setIsFlowOpen(false)
                }}
                chosenMembers={chosenMembers}
            />
            {/* <FirstLoginInfo /> */}
        </div>
    )
}

export default Members
